import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/app/ProtectedRoute";
import { FeedbackWidget } from "@/components/app/FeedbackWidget";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Library from "@/pages/Library";
import LibraryPackage from "@/pages/LibraryPackage";
import Favorites from "@/pages/Favorites";
import Account from "@/pages/Account";
import PromptCreator from "@/pages/PromptCreator";
import RecentlyUsed from "@/pages/RecentlyUsed";
import SavedPrompts from "@/pages/SavedPrompts";
import HelpCenter from "@/pages/HelpCenter";
import PromptMatcher from "@/pages/PromptMatcher";
import AssistantPage from "@/pages/AssistantPage";
import CreatorPreview from "@/pages/CreatorPreview";
import CvLanding from "@/pages/cv/CvLanding";
import CvCreator from "@/pages/cv/CvCreator";
import CvTemplates from "@/pages/cv/CvTemplates";
import CvEmployers from "@/pages/cv/CvEmployers";
import CvApplications from "@/pages/cv/CvApplications";
import CvMyCvs from "@/pages/cv/CvMyCvs";
import CvExport from "@/pages/cv/CvExport";
import CvLetters from "@/pages/cv/CvLetters";
import CvLetterEditor from "@/pages/cv/CvLetterEditor";
import CvMatch from "@/pages/cv/CvMatch";
import BizLanding from "@/pages/business/BizLanding";
import BizGenerator from "@/pages/business/BizGenerator";
import BizAnalysis from "@/pages/business/BizAnalysis";
import BizAnalyses from "@/pages/business/BizAnalyses";
import Contact from "@/pages/Contact";
import PaymentStatus from "@/pages/PaymentStatus";
import PaymentCancelled from "@/pages/PaymentCancelled";
import LegalPage from "@/pages/LegalPage";
import { AdminRoute } from "@/components/admin/AdminRoute";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminMessages from "@/pages/admin/Messages";
import AdminUsers from "@/pages/admin/Users";
import AdminCredits from "@/pages/admin/Credits";
import AdminPayments from "@/pages/admin/Payments";
import AdminAiOperations from "@/pages/admin/AiOperations";
import AdminNoMatchStats from "@/pages/admin/NoMatchStats";
import AdminPricing from "@/pages/admin/Pricing";
import AdminAiCostControl from "@/pages/admin/AiCostControl";
import AdminContent from "@/pages/admin/Content";
import AdminPromptsCms from "@/pages/admin/PromptsCms";
import AdminHelpFaq from "@/pages/admin/HelpFaq";
import AdminSettings from "@/pages/admin/Settings";
import AdminFeedback from "@/pages/admin/Feedback";
import AdminWelcomeBonus from "@/pages/admin/WelcomeBonus";
import { CookieConsent } from "@/components/app/CookieConsent";
import { CreditsProvider } from "@/context/CreditsContext";
import { PlaceholderScreen } from "@/components/app/PlaceholderScreen";
import { RefreshCw, Gift } from "lucide-react";
import { useLenis } from "@/hooks/useLenis";

function AppRoutes() {
  // Handle Google OAuth return: session_id arrives in the URL fragment.
  if (window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/logowanie" element={<Login />} />
            <Route path="/rejestracja" element={<Register />} />
            <Route path="/reset-hasla" element={<ResetPassword />} />
            <Route path="/przypomnij-haslo" element={<ForgotPassword />} />
            <Route path="/weryfikacja-email" element={<VerifyEmail />} />
            <Route
              path="/biblioteka"
              element={
                <ProtectedRoute>
                  <Library />
                </ProtectedRoute>
              }
            />
            <Route
              path="/biblioteka/:slug"
              element={
                <ProtectedRoute>
                  <LibraryPackage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/biblioteka/:slug/:category"
              element={
                <ProtectedRoute>
                  <LibraryPackage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ulubione"
              element={
                <ProtectedRoute>
                  <Favorites />
                </ProtectedRoute>
              }
            />
            <Route
              path="/zapisane-prompty"
              element={
                <ProtectedRoute>
                  <SavedPrompts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ostatnio-uzywane"
              element={
                <ProtectedRoute>
                  <RecentlyUsed />
                </ProtectedRoute>
              }
            />
            <Route
              path="/aktualizacje"
              element={
                <ProtectedRoute>
                  <PlaceholderScreen
                    testId="updates-root"
                    title="Aktualizacje"
                    subtitle="Nowe wersje promptów w Twoich pakietach."
                    emptyTitle="Wszystko jest aktualne"
                    emptyText="Gdy w zakupionych pakietach pojawią się nowe wersje promptów, zobaczysz je tutaj."
                    icon={RefreshCw}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bonusy"
              element={
                <ProtectedRoute>
                  <PlaceholderScreen
                    testId="bonuses-root"
                    title="Bonusy"
                    subtitle="Dodatkowe materiały dołączone do Twoich zakupów."
                    emptyTitle="Brak dostępnych bonusów"
                    emptyText="W Twoich zakupionych produktach nie ma obecnie dodatkowych materiałów."
                    icon={Gift}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pomoc"
              element={
                <ProtectedRoute>
                  <HelpCenter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dopasuj-prompt"
              element={
                <ProtectedRoute>
                  <PromptMatcher />
                </ProtectedRoute>
              }
            />
            <Route path="/asystent" element={<AssistantPage />} />
            <Route path="/kreator-podglad/:slug" element={<CreatorPreview />} />
            <Route path="/cv" element={<CvLanding />} />
            <Route path="/cv/kreator" element={<CvCreator />} />
            <Route path="/cv/kreator/:cvId" element={<CvCreator />} />
            <Route path="/cv/szablony" element={<CvTemplates />} />
            <Route path="/cv/pracodawcy" element={<CvEmployers />} />
            <Route path="/cv/aplikacje" element={<CvApplications />} />
            <Route path="/cv/moje" element={<CvMyCvs />} />
            <Route path="/cv/szablon/:cvId" element={<CvExport />} />
            <Route path="/cv/listy" element={<CvLetters />} />
            <Route path="/cv/list/:letterId" element={<CvLetterEditor />} />
            <Route path="/cv/dopasowanie" element={<CvMatch />} />
            <Route path="/biznes" element={<BizLanding />} />
            <Route path="/biznes/generator" element={<BizGenerator />} />
            <Route path="/biznes/analizy" element={<BizAnalyses />} />
            <Route path="/biznes/analiza/:entry" element={<BizAnalysis />} />
            <Route path="/cv/:scenarioSlug" element={<CvLanding />} />
            <Route path="/kontakt" element={<Contact />} />
            <Route path="/regulamin" element={<LegalPage docKey="regulamin" />} />
            <Route path="/polityka-prywatnosci" element={<LegalPage docKey="polityka-prywatnosci" />} />
            <Route path="/cookies" element={<LegalPage docKey="cookies" />} />
            <Route
              path="/platnosc/status"
              element={
                <ProtectedRoute>
                  <PaymentStatus />
                </ProtectedRoute>
              }
            />
            <Route
              path="/platnosc/anulowano"
              element={
                <ProtectedRoute>
                  <PaymentCancelled />
                </ProtectedRoute>
              }
            />
            <Route
              path="/prompt/:slug"
              element={
                <ProtectedRoute>
                  <PromptCreator />
                </ProtectedRoute>
              }
            />
            <Route
              path="/konto"
              element={
                <ProtectedRoute>
                  <Account />
                </ProtectedRoute>
              }
            />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="wiadomosci" element={<AdminMessages />} />
              <Route path="uzytkownicy" element={<AdminUsers />} />
              <Route path="kredyty" element={<AdminCredits />} />
              <Route path="platnosci" element={<AdminPayments />} />
              <Route path="operacje-ai" element={<AdminAiOperations />} />
              <Route path="braki-asystenta" element={<AdminNoMatchStats />} />
              <Route path="ceny" element={<AdminPricing />} />
              <Route path="kontrola-kosztow" element={<AdminAiCostControl />} />
              <Route path="tresci" element={<AdminContent />} />
              <Route path="cms-promptow" element={<AdminPromptsCms />} />
              <Route path="pomoc" element={<AdminHelpFaq />} />
              <Route path="zgloszenia" element={<AdminFeedback />} />
              <Route path="bonus-powitalny" element={<AdminWelcomeBonus />} />
              <Route path="ustawienia" element={<AdminSettings />} />
            </Route>
          </Routes>
  );
}

function App() {
  useLenis();
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CreditsProvider>
            <AppRoutes />
            <FeedbackWidget />
            <CookieConsent />
          </CreditsProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#141714",
            border: "1px solid #252A26",
            color: "#FFFFFF",
          },
        }}
      />
    </div>
  );
}

export default App;
